import { useEffect, useState } from 'react';
import { getRules } from '../../controllers/RuleController';
import PricingRuleList from './components/PricingRuleList';
import {
    Box,
    CircularProgress,
    Backdrop
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PricingSearch from './components/PricingSearch';
import { getCatalogLocations, getCatalogs } from '../../controllers/CatalogController';
import moment from 'moment';
import Message from '../../components/Message';


export default function PricingRuleContainer() {
    const location = useLocation();
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(false);
    const ruleEnums = useSelector((state) => state.globalData.enums);
    const [filters, setFilters] = useState(() => ({
        catalogId: location.state?.["selectedCatalogId"] || "",
        startDate: moment().format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
    }));
    const [page, setPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [size, setSize] = useState(10);
    const [catalogs, setCatalogs] = useState([]);
    const [locations, setLocations] = useState({});
    const [apiMessage, setApiMessage] = useState({
        open: false,
        type: "success",
        message: "",
    });

    useEffect(() => {
        const fromState = location.state?.["selectedCatalogId"];
        if (fromState == null) return;

        setFilters((prev) => ({
            ...prev,
            catalogId: fromState || prev.catalogId || "",
        }));
    }, [location.state]);

    useEffect(() => {
        getCataloglist();
    }, [])

    useEffect(() => {
        if (filters.catalogId) {
            listRules(filters);
        }
    }, [filters.catalogId, page, size]);

    const getCatalogStoreAndLocations = async (catalogId) => {
        setLoading(true);
        try {
            const res = await getCatalogLocations(catalogId);
            setLocations({ ...res, geoTags: res.geo_tags });
        } catch (err) { console.log(err) }
        finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (filters.catalogId) {
            getCatalogStoreAndLocations(filters.catalogId);
        }
    }, [filters.catalogId]);

    const getCataloglist = async () => {
        try {
            setLoading(true);
            const res = await getCatalogs();
            setCatalogs(res);
            setFilters((prev) => {
                const chosen = prev.catalogId || location.state?.["selectedCatalogId"] || "";
                const exists = res.some(
                    (c) => String(c.catalogId) === String(chosen)
                );

                return {
                    ...prev,
                    catalogId: exists ? chosen : (res[0]?.catalogId || ""),
                };
            });
        } catch (err) {
            console.log("Failed to fetch catalogs", err);
        }
        finally {
            setLoading(false);
        }
    }

    const listRules = async (searchFilters = filters) => {
        setLoading(true);
        let data = {
            ...searchFilters
        };
        if (searchFilters.storeIds?.length > 0) {
            data.storeIds = searchFilters.storeIds.map(store => store.storeId);
        }
        try {
            const res = await getRules({ ...data, page, size });
            const { content = [], totalElements = 0 } = res;
            setRules(content);
            setTotalElements(totalElements);
        } catch (err) {
            setApiMessage({
                open: true,
                type: "error",
                message: err && err.message ? err.message : "Failed to fetch rules",
            });
            setRules([]);
            setTotalElements(0);
            console.log("Failed to fetch rules", err);
        } finally {
            setLoading(false);
        }
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100vh",
                backgroundColor: "#f4f6f8",
            }}
        >
            <Backdrop
                open={loading}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 999,
                    color: "#fff",
                    backdropFilter: "blur(3px)",
                }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            {/* Sticky Search Header */}
            <PricingSearch
                onSearch={listRules}
                filters={filters}
                setFilters={setFilters}
                catalogs={catalogs}
                setPage={setPage}
                locations={locations}
                ruleEnums={ruleEnums}
            />

            {/* Table Section */}
            <Box
                sx={{
                    flex: 1,
                    py: 3,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
            >
                <PricingRuleList
                    rules={rules}
                    ruleEnums={ruleEnums}
                    listRules={listRules}
                    loading={loading}
                    page={page}
                    totalElements={totalElements}
                    size={size}
                    setSize={setSize}
                    setPage={setPage}
                    setLoading={setLoading}
                    setApiMessage={setApiMessage}

                />
            </Box>
            <Message
                open={apiMessage.open}
                type={apiMessage.type}
                message={apiMessage.message}
                onClose={() => setApiMessage((prev) => ({ ...prev, open: false, message: "" }))}
            />
        </Box>
    );
}
